import React from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { MessageSquare, PhoneCall, ShieldCheck, CheckCircle, Phone } from 'lucide-react';
import { buildWaUrl, formatPhoneTel } from '../../lib/contactUtils.ts';

export default function SoftSellingCTA({
  badge,
  title,
  subtitle,
}: {
  badge?: string;
  title?: string;
  subtitle?: string;
}) {
  const { settings } = useApp();
  const primaryColor = settings?.theme.primaryColor || '#059669';

  const ctaConfig = settings?.softsellingCta || {
    badge: 'Fast Response & Advisory Consultation',
    title: 'Plan Your ESG Advisory, Green Tech & Sustainability Project',
    subtitle:
      'Consult on corporate sustainability frameworks, decarbonization audits, or academic green programs with our senior specialists.',
    benefit1: 'Strategic Proposal in 24 Hours',
    benefit2: 'Certified ESG & Sustainability Specialists',
    benefit3: 'Initial Consultation Zero Cost',
    waBtnText: 'Contact Sustainability Advisory',
    phoneBtnText: 'Direct Office Line',
  };

  const displayBadge = badge || ctaConfig.badge || 'Fast Response & Advisory Consultation';
  const displayTitle = title || ctaConfig.title || 'Plan Your ESG Advisory, Green Tech & Sustainability Project';
  const displaySubtitle = subtitle || ctaConfig.subtitle || 'Consult on corporate sustainability frameworks, decarbonization audits, or academic green programs with our senior specialists.';
  const customWaMsg =
    ctaConfig.defaultWaMessage ||
    'Hello Greenjobs ITB Team, I would like to consult on sustainability programs and corporate partnerships.';
  const waConsultUrl = buildWaUrl(
    settings?.contact?.whatsappNumber,
    customWaMsg
  );

  const phoneTel = formatPhoneTel(settings?.contact?.phone);
  const activePhoneDisplay = settings?.contact?.phone || '+62 812-3456-7890';
  const cleanPhoneLabel = (ctaConfig.phoneBtnText || 'Direct Office Line')
    .replace(/\s*\([^)]*\)/g, '')
    .trim();

  return (
    <section id="softselling-cta-section" className="py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl p-6 sm:p-10 md:p-14 text-white"
          style={{ backgroundColor: '#0f172a' }}
        >
          {/* Decorative subtle ambient lights */}
          <div
            className="absolute top-0 right-0 w-80 h-80 rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{ backgroundColor: primaryColor }}
          />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full blur-3xl bg-amber-500/15 pointer-events-none" />

          <div className="relative z-10 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-amber-300 text-[11px] sm:text-xs font-bold mb-3 sm:mb-4 border border-white/10">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>{displayBadge}</span>
            </div>

            <h2 className="text-xl sm:text-3xl md:text-4xl font-extrabold tracking-tight leading-snug sm:leading-tight mb-3 sm:mb-4 text-white">
              {displayTitle}
            </h2>

            <p className="text-slate-300 text-xs sm:text-base leading-relaxed mb-6 sm:mb-8 font-normal">
              {displaySubtitle}
            </p>

            {/* 3 Quick Value Badges */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 mb-6 sm:mb-8 text-xs font-semibold text-slate-200">
              <div className="flex items-center gap-2.5 bg-white/5 p-2.5 sm:p-3 rounded-xl border border-white/5">
                <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="leading-tight">{ctaConfig.benefit1 || 'Strategic Proposal in 24 Hours'}</span>
              </div>
              <div className="flex items-center gap-2.5 bg-white/5 p-2.5 sm:p-3 rounded-xl border border-white/5">
                <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="leading-tight">{ctaConfig.benefit2 || 'Certified ESG Specialists'}</span>
              </div>
              <div className="flex items-center gap-2.5 bg-white/5 p-2.5 sm:p-3 rounded-xl border border-white/5">
                <MessageSquare className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="leading-tight">{ctaConfig.benefit3 || 'Initial Consultation Zero Cost'}</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <a
                id="cta-whatsapp-link"
                href={waConsultUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-3.5 rounded-xl font-extrabold text-white text-xs sm:text-sm shadow-md flex items-center justify-center gap-2 hover:opacity-95 active:scale-98 transition-all"
                style={{ backgroundColor: primaryColor }}
              >
                <PhoneCall className="w-4 h-4" />
                <span>{ctaConfig.waBtnText || 'Contact via WhatsApp'}</span>
              </a>

              <a
                href={`tel:${phoneTel}`}
                className="px-4 py-3 rounded-xl font-bold text-slate-200 hover:text-white bg-white/10 hover:bg-white/15 text-xs sm:text-sm transition-colors border border-white/10 flex items-center justify-center gap-2"
                title={`Office Line: ${activePhoneDisplay}`}
              >
                <Phone className="w-3.5 h-3.5 text-amber-400" />
                <span>{cleanPhoneLabel} ({activePhoneDisplay})</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
